import React, { useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../../components/Content";
import { Typography, Box, CircularProgress, Alert, Button } from "@mui/material";
import { useEvacuationPlanById } from "../../../hooks/useEvacuationData";
import { useDeleteEvacuationPlanWithConfirm } from "../../../hooks/useDeleteEvacuationPlanWithConfirm";
import { useAlarmTypeLabel } from "../../../hooks/useAlarmTypeLabel";
import { EvacuationPlanHeader } from "../../../components/evacuation/EvacuationPlanHeader";
import { EvacuationPlanInfo } from "../../../components/evacuation/EvacuationPlanInfo";
import { EvacuationPlanSteps } from "../../../components/evacuation/EvacuationPlanSteps";

export const EvacuationDetailsView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useEvacuationPlanById(id);
  const { handleDelete, isDeleting } = useDeleteEvacuationPlanWithConfirm();
  
  const evacuationPlan = data?.data;
  const { label: alarmTypeLabel } = useAlarmTypeLabel(evacuationPlan?.emergency_type);

  const handleBack = useCallback(() => {
    navigate("/admin/evacuation");
  }, [navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/admin/evacuation/edit/${id}`);
  }, [navigate, id]);

  const handleDeleteClick = useCallback(() => {
    if (id) {
      handleDelete(id);
    }
  }, [id, handleDelete]);

  if (isLoading) {
    return (
      <Content title="Laddar...">
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </Content>
    );
  }

  if (isError) {
    return (
      <Content title="Fel">
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error
              ? error.message
              : "Ett fel uppstod vid hämtning av checklistan."}
          </Alert>
          <Button variant="contained" onClick={() => refetch()}>
            Försök igen
          </Button>
        </Box>
      </Content>
    );
  }

  if (!evacuationPlan) {
    return (
      <Content title="Inte hittad">
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">
            Checklistan kunde inte hittas.
          </Alert>
        </Box>
      </Content>
    );
  }

  return (
    <Content title={`Checklista: ${evacuationPlan.name}`}>
      <Box sx={{ p: 3 }}>
        <EvacuationPlanHeader
          id={id!}
          onBack={handleBack}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isDeleting={isDeleting}
        />

        <EvacuationPlanInfo
          name={evacuationPlan.name}
          alarmTypeLabel={alarmTypeLabel}
          organisationName={evacuationPlan.organisation_name || ""}
          isActive={evacuationPlan.is_active}
          stepsCount={evacuationPlan.steps?.length || 0}
        />

        <EvacuationPlanSteps steps={evacuationPlan.steps || []} />
      </Box>
    </Content>
  );
};
